import { useState, useEffect, useCallback, useMemo } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { milestonesAPI, modulesAPI, usersAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  ChevronLeft, 
  ChevronRight, 
  Filter, 
  Search, 
  Plus,
  X,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import MilestoneModal from '../components/MilestoneModal';

const STATUS_COLORS = {
  not_started: { bg: '#F3F4F6', text: '#6B7280', label: 'Not Started' },
  in_progress: { bg: '#FEF3C7', text: '#D97706', label: 'In Progress' },
  completed: { bg: '#D1FAE5', text: '#059669', label: 'Completed' },
  testing: { bg: '#DBEAFE', text: '#2563EB', label: 'Testing' },
  blocked: { bg: '#1F2937', text: '#FFFFFF', label: 'Blocked' },
  restaurant_integration: { bg: '#E9D5FF', text: '#7C3AED', label: 'Integration' },
};

const MODULE_STATUS_COLORS = {
  active: '#22C55E',
  in_development: '#EAB308',
  planned: '#3B82F6',
  backlog: '#9CA3AF',
};

const PRIORITY_COLORS = {
  high: '#DC2626',
  medium: '#F59E0B',
  low: '#22C55E',
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function RoadmapPage() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [modules, setModules] = useState([]);
  const [developers, setDevelopers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('month');
  const [currentDate, setCurrentDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [selectedTask, setSelectedTask] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    module: 'all',
    status: 'all',
    priority: 'all',
    assignee: 'all',
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [expandedModules, setExpandedModules] = useState({});
  const [hoveredTask, setHoveredTask] = useState(null);
  const [hoverPos, setHoverPos] = useState({ x: 0, y: 0 });

  const fetchData = useCallback(async () => {
    try {
      const [tasksRes, modulesRes, usersRes] = await Promise.all([
        milestonesAPI.getAll(),
        modulesAPI.getAll(),
        user?.role === 'admin' ? usersAPI.getAll() : Promise.resolve({ data: [] }),
      ]);
      setTasks(tasksRes.data);
      setModules(modulesRes.data);
      setAllUsers(usersRes.data);
      
      // Get unique assignees from tasks
      const uniqueAssignees = [];
      const seenIds = new Set();
      tasksRes.data.forEach(t => {
        (t.assignees || []).forEach(a => {
          if (a.id && !seenIds.has(a.id)) {
            seenIds.add(a.id);
            uniqueAssignees.push({ id: a.id, name: a.name });
          }
        });
      });
      setDevelopers(uniqueAssignees);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  }, [user?.role]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const toggleModule = (moduleId) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  const expandAllModules = () => {
    const allExpanded = {};
    modules.forEach(m => { allExpanded[m.id] = true; });
    setExpandedModules(allExpanded);
  };

  const collapseAllModules = () => {
    setExpandedModules({});
  };

  const getTimelineRange = () => {
    const start = new Date(currentDate);
    const end = new Date(currentDate);
    
    if (view === 'week') {
      start.setDate(start.getDate() - start.getDay());
      end.setDate(start.getDate() + 6);
    } else if (view === 'month') {
      start.setMonth(start.getMonth() - 1);
      end.setMonth(end.getMonth() + 4);
    } else {
      start.setMonth(Math.floor(start.getMonth() / 3) * 3);
      end.setMonth(start.getMonth() + 11);
    }
    
    return { start, end };
  };

  const getTimelineColumns = () => {
    const { start, end } = getTimelineRange();
    const columns = [];
    const current = new Date(start);
    
    while (current <= end) {
      if (view === 'week') {
        columns.push({
          key: current.toISOString().split('T')[0],
          label: current.getDate().toString(),
          date: new Date(current),
        });
        current.setDate(current.getDate() + 1);
      } else if (view === 'month') {
        columns.push({
          key: `${current.getFullYear()}-${current.getMonth()}`,
          label: `${MONTHS[current.getMonth()]} ${current.getFullYear()}`,
          date: new Date(current),
        });
        current.setMonth(current.getMonth() + 1);
      } else {
        columns.push({
          key: `Q${Math.floor(current.getMonth() / 3) + 1}-${current.getFullYear()}`,
          label: `Q${Math.floor(current.getMonth() / 3) + 1} ${current.getFullYear()}`,
          date: new Date(current),
        });
        current.setMonth(current.getMonth() + 3);
      }
    }
    
    return columns;
  };

  const filteredTasks = useMemo(() => {
    return tasks.filter(task => {
      if (searchQuery && !task.title.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      if (filters.module !== 'all' && task.module_id !== filters.module) return false;
      if (filters.status !== 'all' && task.status !== filters.status) return false;
      if (filters.priority !== 'all' && task.priority !== filters.priority) return false;
      if (filters.assignee !== 'all' && !(task.assignee_ids || []).includes(filters.assignee)) return false;
      return true;
    });
  }, [tasks, filters, searchQuery]);

  const getTaskPosition = (task, columns) => {
    if (!task.start_date || !task.end_date) return null;
    
    const startDate = new Date(task.start_date);
    const endDate = new Date(task.end_date);
    const { start: timelineStart, end: timelineEnd } = getTimelineRange();
    
    if (endDate < timelineStart || startDate > timelineEnd) return null;
    
    const totalDays = (timelineEnd - timelineStart) / (1000 * 60 * 60 * 24);
    const startOffset = Math.max(0, (startDate - timelineStart) / (1000 * 60 * 60 * 24));
    const duration = Math.min(totalDays - startOffset, (endDate - startDate) / (1000 * 60 * 60 * 24) + 1);
    
    const left = (startOffset / totalDays) * 100;
    const width = Math.max(5, (duration / totalDays) * 100);
    
    return { left: `${left}%`, width: `${width}%` };
  };

  const getTodayPosition = (columns) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const { start: timelineStart, end: timelineEnd } = getTimelineRange();
    
    if (today < timelineStart || today > timelineEnd) return null;
    
    const totalDays = (timelineEnd - timelineStart) / (1000 * 60 * 60 * 24);
    const offset = (today - timelineStart) / (1000 * 60 * 60 * 24);
    
    return `${(offset / totalDays) * 100}%`;
  };

  // Group overlapping tasks and assign row positions
  const getTasksWithRows = (moduleTasks, columns) => {
    const tasksWithPos = moduleTasks
      .map(task => {
        const position = getTaskPosition(task, columns);
        return position ? { ...task, position } : null;
      })
      .filter(Boolean)
      .sort((a, b) => parseFloat(a.position.left) - parseFloat(b.position.left));
    
    const rows = [];
    tasksWithPos.forEach(task => {
      const taskLeft = parseFloat(task.position.left);
      const taskRight = taskLeft + parseFloat(task.position.width);
      
      let assignedRow = -1;
      for (let i = 0; i < rows.length; i++) {
        const lastTaskInRow = rows[i][rows[i].length - 1];
        const lastRight = parseFloat(lastTaskInRow.position.left) + parseFloat(lastTaskInRow.position.width);
        
        if (taskLeft >= lastRight) {
          assignedRow = i;
          break;
        }
      }
      
      if (assignedRow === -1) {
        assignedRow = rows.length;
        rows.push([]);
      }
      
      rows[assignedRow].push({ ...task, row: assignedRow });
    });
    
    return { tasks: rows.flat(), rowCount: rows.length };
  };

  const handleTaskUpdate = async () => {
    await fetchData();
    setSelectedTask(null);
    setShowCreateModal(false);
  };

  const columns = getTimelineColumns();
  const todayPosition = getTodayPosition(columns);

  if (loading) {
    return (
      <DashboardLayout pageTitle="Roadmap">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#CFFF40] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout pageTitle="Roadmap">
      <div className="space-y-4" data-testid="roadmap-page">
        {/* Top Controls */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2">
            {/* View Switcher */}
            <div className="flex bg-white border border-[#E0E0E0] rounded-lg overflow-hidden">
              {['quarter', 'month', 'week'].map((v) => (
                <button
                  key={v}
                  onClick={() => setView(v)}
                  data-testid={`view-${v}`}
                  className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-colors ${
                    view === v 
                      ? 'bg-[#CFFF40] text-[#101010]' 
                      : 'text-[#666666] hover:bg-[#F5F5F5]'
                  }`}
                >
                  {v.charAt(0).toUpperCase() + v.slice(1)}
                </button>
              ))}
            </div>
            
            {/* Navigation */}
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                onClick={() => {
                  const newDate = new Date(currentDate);
                  if (view === 'week') newDate.setDate(newDate.getDate() - 7);
                  else if (view === 'month') newDate.setMonth(newDate.getMonth() - 1);
                  else newDate.setMonth(newDate.getMonth() - 3);
                  setCurrentDate(newDate);
                }}
                data-testid="nav-prev"
              >
                <ChevronLeft size={16} />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => {
                  const newDate = new Date(currentDate);
                  if (view === 'week') newDate.setDate(newDate.getDate() + 7);
                  else if (view === 'month') newDate.setMonth(newDate.getMonth() + 1);
                  else newDate.setMonth(newDate.getMonth() + 3);
                  setCurrentDate(newDate);
                }}
                data-testid="nav-next"
              >
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Search */}
            <div className="relative flex-1 sm:flex-none">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#666666]" size={16} />
              <Input
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-full sm:w-64"
                data-testid="search-input"
              />
            </div>
            
            {/* Filter Button */}
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              data-testid="filter-btn"
              className="flex-shrink-0"
            >
              <Filter size={16} className="sm:mr-2" />
              <span className="hidden sm:inline">Filters</span>
            </Button>
            
            {/* Add Milestone Button (Admin only) */}
            {user?.role === 'admin' && (
              <Button
                onClick={() => setShowCreateModal(true)}
                className="bg-[#FF6B35] hover:bg-[#e55a2b] text-white"
                data-testid="add-task-btn"
              >
                <Plus size={16} className="mr-2" />
                Add Milestone
              </Button>
            )}
          </div>
        </div>
        
        {/* Filters Panel */}
        {showFilters && (
          <div className="bg-white border border-[#E0E0E0] rounded-lg p-4 flex flex-wrap gap-3" data-testid="filters-panel">
            <select
              value={filters.module}
              onChange={(e) => setFilters(f => ({ ...f, module: e.target.value }))}
              className="px-3 py-2 border border-[#E0E0E0] rounded-md text-sm bg-white flex-1 min-w-[140px] sm:flex-none sm:min-w-[180px]"
            >
              <option value="all">All Modules</option>
              {modules.map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
            
            <select
              value={filters.status}
              onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
              className="px-3 py-2 border border-[#E0E0E0] rounded-md text-sm bg-white flex-1 min-w-[140px] sm:flex-none sm:min-w-[180px]"
            >
              <option value="all">All Statuses</option>
              {Object.entries(STATUS_COLORS).map(([key, val]) => (
                <option key={key} value={key}>{val.label}</option>
              ))}
            </select>
            
            <select
              value={filters.priority}
              onChange={(e) => setFilters(f => ({ ...f, priority: e.target.value }))}
              className="px-3 py-2 border border-[#E0E0E0] rounded-md text-sm bg-white flex-1 min-w-[140px] sm:flex-none sm:min-w-[180px]"
            >
              <option value="all">All Priorities</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
            
            <select
              value={filters.assignee}
              onChange={(e) => setFilters(f => ({ ...f, assignee: e.target.value }))}
              className="px-3 py-2 border border-[#E0E0E0] rounded-md text-sm bg-white flex-1 min-w-[140px] sm:flex-none sm:min-w-[180px]"
            >
              <option value="all">All Assignees</option>
              {developers.map(d => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
            
            <Button
              variant="ghost"
              onClick={() => setFilters({ module: 'all', status: 'all', priority: 'all', assignee: 'all' })}
              data-testid="clear-filters"
            >
              <X size={16} className="mr-2" />
              Clear
            </Button>
          </div>
        )}
        
        {/* Roadmap Grid */}
        <div className="bg-white border border-[#E0E0E0] rounded-lg overflow-hidden">
          <div className="flex">
            {/* Module List (Left Panel) */}
            <div className="w-[140px] sm:w-[200px] flex-shrink-0 border-r border-[#E0E0E0]">
              <div className="h-12 border-b border-[#E0E0E0] flex items-center justify-between px-4 bg-[#F5F5F5]">
                <span className="text-sm font-medium text-[#666666]">Modules</span>
                <div className="flex gap-1">
                  <button
                    onClick={expandAllModules}
                    className="p-1 hover:bg-[#E0E0E0] rounded text-[#666666]"
                    title="Expand All"
                  >
                    <ChevronDown size={14} />
                  </button>
                  <button
                    onClick={collapseAllModules}
                    className="p-1 hover:bg-[#E0E0E0] rounded text-[#666666]"
                    title="Collapse All"
                  >
                    <ChevronUp size={14} />
                  </button>
                </div>
              </div>
              <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 300px)' }} id="roadmap-sidebar">
                {modules.map(module => {
                  const moduleTasks = filteredTasks.filter(t => t.module_id === module.id);
                  const { rowCount } = getTasksWithRows(moduleTasks, columns);
                  const isExpanded = expandedModules[module.id];
                  const moduleHeight = isExpanded ? Math.max(1, rowCount) * 48 + 24 : 48;
                  
                  return (
                    <div
                      key={module.id}
                      className="border-b border-[#E0E0E0] flex items-center px-4 gap-2 hover:bg-[#F5F5F5] cursor-pointer transition-all overflow-hidden"
                      style={{ height: moduleHeight }}
                      onClick={() => toggleModule(module.id)}
                      data-testid={`module-row-${module.id}`}
                    >
                      <button className="text-[#666666] flex-shrink-0">
                        {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </button>
                      <div
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: MODULE_STATUS_COLORS[module.status] || '#9CA3AF' }}
                      />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm text-[#111111] truncate block">{module.name}</span>
                        {moduleTasks.length > 0 && (
                          <span className="text-xs text-[#666666]">{moduleTasks.length} milestones</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Timeline Grid */}
            <div className="flex-1 overflow-x-auto">
              {/* Timeline Header */}
              <div className="flex border-b border-[#E0E0E0] bg-[#F5F5F5]">
                {columns.map(col => (
                  <div
                    key={col.key}
                    className="flex-1 min-w-[100px] h-12 flex items-center justify-center border-r border-[#E0E0E0] last:border-r-0"
                  >
                    <span className="text-xs font-medium text-[#666666]">{col.label}</span>
                  </div>
                ))}
              </div>
              
              {/* Timeline Rows */}
              <div className="relative overflow-y-auto" style={{ maxHeight: 'calc(100vh - 300px)' }}
                onScroll={(e) => {
                  const sidebar = document.getElementById('roadmap-sidebar');
                  if (sidebar) sidebar.scrollTop = e.target.scrollTop;
                }}
              >
                {/* Today Line */}
                {todayPosition && (
                  <div
                    className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-10"
                    style={{ left: todayPosition }}
                    data-testid="today-line"
                  >
                    <div className="absolute -top-1 left-1/2 -translate-x-1/2 bg-red-500 text-white text-xs px-1 rounded">
                      Today
                    </div>
                  </div>
                )}
                
                {/* Module Rows */}
                {modules.map((module) => {
                  const moduleTasks = filteredTasks.filter(t => t.module_id === module.id);
                  const { tasks: tasksWithRows, rowCount } = getTasksWithRows(moduleTasks, columns);
                  const isExpanded = expandedModules[module.id];
                  const moduleHeight = isExpanded ? Math.max(1, rowCount) * 48 + 24 : 48;
                  
                  return (
                    <div
                      key={module.id}
                      className="border-b border-[#E0E0E0] relative transition-all overflow-hidden"
                      style={{ height: moduleHeight }}
                    >
                      {/* Grid lines */}
                      <div className="absolute inset-0 flex">
                        {columns.map(col => (
                          <div key={col.key} className="flex-1 min-w-[100px] border-r border-[#E0E0E0] last:border-r-0" />
                        ))}
                      </div>
                      
                      {/* Tasks */}
                      {isExpanded && tasksWithRows.map(task => {
                        const statusColor = STATUS_COLORS[task.status] || STATUS_COLORS.not_started;
                        const priorityColor = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium;
                        const topOffset = 8 + (task.row * 48);
                        
                        return (
                          <div
                            key={task.id}
                            className="absolute h-10 cursor-pointer transition-all hover:scale-[1.02] hover:shadow-md z-[5]"
                            style={{
                              left: task.position.left,
                              width: task.position.width,
                              minWidth: '80px',
                              top: topOffset,
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTask(task);
                            }}
                            onMouseEnter={(e) => {
                              const rect = e.currentTarget.getBoundingClientRect();
                              setHoverPos({ x: rect.left + rect.width / 2, y: rect.top });
                              setHoveredTask(task);
                            }}
                            onMouseLeave={() => setHoveredTask(null)}
                            data-testid={`task-card-${task.id}`}
                          >
                            <div
                              className="h-full rounded-md border overflow-hidden flex"
                              style={{
                                backgroundColor: statusColor.bg,
                                borderColor: statusColor.text,
                              }}
                            >
                              {/* Priority Bar */}
                              <div
                                className="w-1 flex-shrink-0"
                                style={{ backgroundColor: priorityColor }}
                              />
                              
                              <div className="flex-1 px-2 py-1 overflow-hidden">
                                <div className="flex items-center gap-1">
                                  <span
                                    className="text-xs font-medium truncate"
                                    style={{ color: statusColor.text }}
                                  >
                                    {task.title.length > 30 ? task.title.slice(0, 30) + '...' : task.title}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  {task.assignees?.length > 0 && (
                                    <div className="w-5 h-5 rounded-full bg-[#FFFF66] text-[#101010] flex items-center justify-center text-[10px] font-bold" title={task.assignees.map(a => a.name).join(', ')}>
                                      {task.assignees[0]?.initials}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Task Hover Preview */}
      {hoveredTask && (
        <div
          className="fixed z-50 pointer-events-none"
          style={{
            left: Math.min(hoverPos.x - 140, window.innerWidth - 300),
            top: Math.max(hoverPos.y - 160, 8),
          }}
          data-testid="task-hover-preview"
        >
          <div className="bg-white border border-[#E0E0E0] rounded-lg shadow-xl p-3 w-[280px]">
            <div className="flex items-start justify-between gap-2 mb-2">
              <h4 className="font-semibold text-sm text-[#111111] leading-tight">{hoveredTask.title}</h4>
              <Badge
                style={{ backgroundColor: (STATUS_COLORS[hoveredTask.status] || STATUS_COLORS.not_started).bg, color: (STATUS_COLORS[hoveredTask.status] || STATUS_COLORS.not_started).text, fontSize: '10px' }}
              >
                {(STATUS_COLORS[hoveredTask.status] || STATUS_COLORS.not_started).label}
              </Badge>
            </div>
            {hoveredTask.description && (
              <p className="text-xs text-[#666666] mb-2 line-clamp-2">{hoveredTask.description}</p>
            )}
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
              <div className="text-[#999999]">Priority</div>
              <div className="font-medium capitalize" style={{ color: PRIORITY_COLORS[hoveredTask.priority] || PRIORITY_COLORS.medium }}>{hoveredTask.priority}</div>
              <div className="text-[#999999]">Module</div>
              <div className="font-medium text-[#111111] truncate">{hoveredTask.module_name || '-'}</div>
              <div className="text-[#999999]">Dates</div>
              <div className="font-medium text-[#111111]">{hoveredTask.start_date || '-'} — {hoveredTask.end_date || '-'}</div>
              {hoveredTask.assignees?.length > 0 && (
                <>
                  <div className="text-[#999999]">Assigned</div>
                  <div className="font-medium text-[#111111] truncate">{hoveredTask.assignees.map(a => a.name).join(', ')}</div>
                </>
              )}
              {hoveredTask.estimated_hours > 0 && (
                <>
                  <div className="text-[#999999]">Est. Hours</div>
                  <div className="font-medium text-[#111111]">{hoveredTask.estimated_hours}h</div>
                </>
              )}
              {hoveredTask.story_points > 0 && (
                <>
                  <div className="text-[#999999]">Story Points</div>
                  <div className="font-medium text-[#111111]">{hoveredTask.story_points}</div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Task Modal */}
      {selectedTask && (
        <MilestoneModal
          task={selectedTask}
          modules={modules}
          users={allUsers}
          onClose={() => setSelectedTask(null)}
          onUpdate={handleTaskUpdate}
          isAdmin={user?.role === 'admin'}
          isDeveloper={user?.role === 'developer'}
        />
      )}
      
      {/* Create Task Modal */}
      {showCreateModal && (
        <MilestoneModal
          task={null}
          modules={modules}
          users={allUsers}
          onClose={() => setShowCreateModal(false)}
          onUpdate={handleTaskUpdate}
          isAdmin={true}
          isDeveloper={false}
        />
      )}
    </DashboardLayout>
  );
}
